import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const AdminChatViewing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const messagesEndRef = useRef(null);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    // Get conversation data from navigation state
    if (location.state?.conversation) {
      setConversation(location.state.conversation);
      setLoading(false);
    } else {
      // If no data passed, redirect back to conversations page
      setLoading(false);
      // Optionally, you could fetch the conversation by ID here
      // For now, we'll just show an error
    }
  }, [location.state, id]);

  useEffect(() => {
    // Scroll to bottom when messages load
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleEditClick = (chatId, currentContent) => {
    setEditingMessageId(chatId);
    setEditedContent(currentContent || "");
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent("");
  };

  const handleSaveEdit = async (chatId) => {
    if (!editedContent.trim()) return;

    setSavingEdit(true);
    try {
      // API call to save the corrected response
      const url =
        process.env.REACT_APP_ADMIN_URL_EDIT_AI_RESPONSE ||
        "/api/admin/correct-response";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId: conversation.convId,
          messageId: chatId,
          correctedContent: editedContent.trim(),
          correctedTimestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        // Update local state to reflect the change
        const updatedChatList = conversation.chatList.map((chat) => {
          if ((chat.id || chat.created_at) === chatId) {
            return {
              ...chat,
              message: {
                ...chat.message,
                content: editedContent.trim(),
                corrected: true,
                correctedAt: new Date().toISOString(),
              },
            };
          }
          return chat;
        });

        setConversation({
          ...conversation,
          chatList: updatedChatList,
        });

        // Clear editing state
        setEditingMessageId(null);
        setEditedContent("");
      } else {
        console.error("Failed to save correction");
        // Optionally show an error message
      }
    } catch (error) {
      console.error("Error saving correction:", error);
      // Optionally show an error message
    } finally {
      setSavingEdit(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    return date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    if (messageDay.getTime() === today.getTime()) {
      return "Today";
    }

    if (messageDay.getTime() === yesterday.getTime()) {
      return "Yesterday";
    }

    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();

    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${dayName}, ${getOrdinal(day)} ${month} ${year}`;
  };

  // Group messages by date
  const groupMessagesByDate = (chats) => {
    const groups = {};

    chats.forEach((chat) => {
      const dateKey = formatDate(chat.created_at);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(chat);
    });

    // Sort messages within each date group (oldest first)
    Object.keys(groups).forEach((key) => {
      groups[key].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
    });

    return groups;
  };

  // Sort dates function (oldest first)
  const sortDatesOldestFirst = (dateGroups) => {
    const dateKeys = Object.keys(dateGroups);

    // Sort dates based on the first message's timestamp in each group
    dateKeys.sort((a, b) => {
      const firstMessageA = dateGroups[a][0];
      const firstMessageB = dateGroups[b][0];
      return (
        new Date(firstMessageA.created_at) - new Date(firstMessageB.created_at)
      );
    });

    return dateKeys;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <svg
                className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-500"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-400">Loading conversation...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen pt-20 pb-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-12 text-center">
            <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-400 to-red-600 
                          rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30"
            >
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Conversation Not Found
            </h2>
            <p className="text-gray-400 mb-6">
              Unable to load conversation data
            </p>
            <button
              onClick={() => navigate("/conversations")}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 
                       text-white hover:brightness-125 transition-all duration-300"
            >
              Back to Conversations
            </button>
          </div>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(conversation.chatList || []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="glass border-b border-blue-500/20 sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/conversations")}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
              >
                <svg
                  className="w-5 h-5 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div>
                <h1 className="text-lg font-semibold text-white line-clamp-1">
                  {conversation.title || "Conversation"}
                </h1>
                <div className="flex items-center space-x-3 text-xs text-gray-400">
                  <span>{conversation.messageCount} messages</span>
                  <span>•</span>
                  <span
                    className={`px-2 py-0.5 rounded-full ${
                      conversation.status === "web"
                        ? "bg-green-500/20 text-green-300"
                        : "bg-blue-500/20 text-blue-300"
                    }`}
                  >
                    {conversation.status === "telegram" ? "Telegram" : "Web"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-grow overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {Object.keys(messageGroups).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No messages in this conversation</p>
            </div>
          ) : (
            <>
              {sortDatesOldestFirst(messageGroups).map((date) => {
                const messages = messageGroups[date];
                return (
                  <div key={date} className="mb-6">
                    {/* Date Separator */}
                    <div className="flex items-center justify-center mb-4">
                      <div className="px-3 py-1 bg-gray-800/50 rounded-full">
                        <p className="text-xs text-gray-400">{date}</p>
                      </div>
                    </div>

                    {/* Messages for this date - already sorted oldest to newest */}
                    {messages.map((chat, index) => (
                      <div
                        key={`${chat.id || index}-${chat.created_at}`}
                        className={`flex ${
                          chat.message?.type === "human"
                            ? "justify-end"
                            : "justify-start"
                        } mb-4 animate-fadeIn`}
                      >
                        <div
                          className={`flex items-end space-x-2 max-w-[85%] sm:max-w-[75%] ${
                            chat.message?.type === "human"
                              ? "flex-row-reverse space-x-reverse"
                              : ""
                          }`}
                        >
                          {/* Avatar */}
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              chat.message?.type === "human"
                                ? "bg-gradient-to-br from-purple-500 to-purple-600"
                                : "bg-gradient-to-br from-blue-500 to-blue-600"
                            }`}
                          >
                            {chat.message?.type === "human" ? (
                              <span className="text-white text-xs font-semibold">
                                U
                              </span>
                            ) : (
                              <span className="text-white text-xs font-semibold">
                                AI
                              </span>
                            )}
                          </div>

                          {/* Message Bubble */}
                          <div>
                            <div
                              className={`rounded-2xl px-4 py-3 ${
                                chat.message?.type === "human"
                                  ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white"
                                  : "glass border border-blue-500/20 text-gray-100"
                              }`}
                            >
                              {/* Message Content */}
                              {editingMessageId ===
                              (chat.id || chat.created_at) ? (
                                // Edit Mode
                                <div className="space-y-3">
                                  <textarea
                                    value={editedContent}
                                    onChange={(e) =>
                                      setEditedContent(e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-gray-800/50 text-white rounded-lg 
                                           border border-blue-500/30 focus:border-blue-400 
                                           focus:ring-2 focus:ring-blue-400/20 focus:outline-none
                                           resize-none min-h-[100px]"
                                    autoFocus
                                  />
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={handleCancelEdit}
                                      disabled={savingEdit}
                                      className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 
                                             text-white transition-colors disabled:opacity-50"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleSaveEdit(
                                          chat.id || chat.created_at
                                        )
                                      }
                                      disabled={
                                        savingEdit || !editedContent.trim()
                                      }
                                      className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 
                                             text-white transition-colors disabled:opacity-50"
                                    >
                                      {savingEdit ? "Saving..." : "Save"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // Display Mode
                                <>
                                  <div className="flex items-start justify-between">
                                    <p className="text-sm whitespace-pre-wrap break-words flex-grow">
                                      {chat.message?.content || "No content"}
                                    </p>
                                    {/* Edit button for AI messages */}
                                    {chat.message?.type === "ai" && (
                                      <button
                                        onClick={() =>
                                          handleEditClick(
                                            chat.id || chat.created_at,
                                            chat.message?.content
                                          )
                                        }
                                        className="ml-2 p-1 rounded hover:bg-white/10 transition-colors group"
                                        title="Edit response"
                                      >
                                        <svg
                                          className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                  </div>

                                  {/* Corrected indicator */}
                                  {chat.message?.corrected && (
                                    <div className="mt-2 pt-2 border-t border-white/10">
                                      <p className="text-xs text-yellow-400 italic">
                                        ✏️ Edited by admin
                                      </p>
                                    </div>
                                  )}

                                  {/* Additional Info for AI messages */}
                                  {chat.message?.type === "ai" &&
                                    chat.products &&
                                    chat.products.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-white/20">
                                        <p className="text-xs text-gray-300 mb-2">
                                          Related Products:
                                        </p>
                                        <div className="space-y-1">
                                          {chat.products
                                            .slice(0, 3)
                                            .map((product, idx) => (
                                              <div
                                                key={idx}
                                                className="text-xs text-gray-400"
                                              >
                                                •{" "}
                                                {product.name ||
                                                  product.title ||
                                                  "Product"}
                                              </div>
                                            ))}
                                          {chat.products.length > 3 && (
                                            <div className="text-xs text-gray-500">
                                              +{chat.products.length - 3} more
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </>
                              )}
                            </div>

                            {/* Timestamp */}
                            <p
                              className={`text-xs text-gray-500 mt-1 ${
                                chat.message?.type === "human"
                                  ? "text-right"
                                  : "text-left"
                              }`}
                            >
                              {formatMessageTime(chat.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Footer Info Bar */}
      <div className="glass border-t border-blue-500/20 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Viewing conversation: {conversation.convId}</span>
            <span>Channel: {conversation.status}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminChatViewing;
